<template>
  <div class="st" :class="{ 'st--started': started }">
    <!-- Step list: tabs on desktop, numbered segments on phones. -->
    <div class="st-steps" role="tablist" :aria-label="ariaLabel">
      <button
        v-for="(step, i) in tour.steps"
        :id="`${uid}-tab-${step.id}`"
        :key="step.id"
        type="button"
        role="tab"
        class="st-step"
        :class="{ 'is-active': i === index, 'is-done': i < index }"
        :aria-selected="i === index"
        :aria-controls="`${uid}-panel`"
        :tabindex="i === index ? 0 : -1"
        :aria-label="`${i + 1}. ${step.label}`"
        @click="go(i)"
        @keydown="onTabKey($event, i)"
      >
        <span class="st-step-num" aria-hidden="true">{{ i + 1 }}</span>
        <span class="st-step-label">{{ step.label }}</span>
      </button>
    </div>

    <div :id="`${uid}-panel`" class="st-panel" role="tabpanel" tabindex="0" :aria-labelledby="`${uid}-tab-${current.id}`">
      <!-- The real screens, stacked: only the current one is visible, so stepping never moves the page. -->
      <figure class="st-stage">
        <div class="st-bar" aria-hidden="true"><span></span><span></span><span></span></div>
        <div class="st-screens">
          <picture
            v-for="(step, i) in tour.steps"
            :key="step.id"
            class="st-shot"
            :class="{ 'is-current': i === index, 'st-shot--desktop-only': !step.phone }"
            :aria-hidden="i !== index"
          >
            <source v-if="step.phone" media="(max-width: 899px)" :srcset="step.phone.src" :width="step.phone.width" :height="step.phone.height" />
            <img
              :src="step.desktop.src"
              :width="step.desktop.width"
              :height="step.desktop.height"
              :alt="step.desktop.alt"
              loading="lazy"
              decoding="async"
            />
          </picture>
        </div>
      </figure>

      <div class="st-copy">
        <p class="st-count">{{ fill(countLabel, { n: index + 1, total: tour.steps.length }) }}</p>
        <h3 class="st-caption">{{ current.caption }}</h3>
        <p class="st-sub">{{ current.subCaption }}</p>

        <div class="st-controls">
          <button type="button" class="st-btn st-btn--ghost" :disabled="index === 0" @click="go(index - 1)">{{ backLabel }}</button>
          <button v-if="!isLast" type="button" class="st-btn st-btn--next" @click="go(index + 1)">{{ fill(nextLabel, { label: tour.steps[index + 1].label }) }}</button>
          <!-- v-show, not v-if: the phone sticky bar collects its hide targets once on mount, and must stay
               off while this button or its terms are on screen (one trial button at a time). -->
          <a
            v-show="isLast"
            class="st-btn st-btn--trial"
            data-sticky-cta-hide
            :href="addReferralToUrl(trialUrl)"
            data-conversion-event="start_free_click"
            :data-conversion-label="`${conversionLabel} tour CTA`"
            :data-conversion-location="location"
          >{{ trialLabel }}</a>
        </div>
        <!-- The trial terms sit directly under the trial button (legal B1.1). -->
        <div v-show="isLast" class="st-terms" data-sticky-cta-hide><TrialDisclosure :plan="plan" /></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ScreenTour } from '~/data/walkthroughs/screen-tours'

const props = withDefaults(
  defineProps<{
    tour: ScreenTour
    /** data-conversion-location for the trial button and the page_path context of the tour events. */
    location: string
    conversionLabel: string
    ariaLabel?: string
    trialUrl?: string
    plan?: string
    /** Button and counter text; natively localized pages (/pricing) pass tr()'d strings. */
    backLabel?: string
    nextLabel?: string
    trialLabel?: string
    countLabel?: string
  }>(),
  {
    ariaLabel: 'Tour steps',
    trialUrl: 'https://app.workspace369.com/signup',
    plan: undefined,
    backLabel: 'Back',
    nextLabel: 'Next: {label}',
    trialLabel: 'Start free trial',
    countLabel: '{n} of {total}',
  },
)

const fill = (text: string, values: Record<string, string | number>) =>
  text.replace(/\{(\w+)\}/g, (match, key) => (key in values ? String(values[key]) : match))

const { addReferralToUrl } = useReferral()
const { consent } = useCookieConsent()
const uid = useId()
const index = ref(0)
const started = ref(false)
const current = computed(() => props.tour.steps[index.value])
const isLast = computed(() => index.value === props.tour.steps.length - 1)

// tour_start on the first step change and tour_complete on reaching the last step, once each per page view,
// only with analytics consent (same delivery as the CTA test's exposure event).
const sent = new Set<string>()
const track = (name: 'tour_start' | 'tour_complete') => {
  if (sent.has(name) || consent.value?.analytics !== true) return
  sent.add(name)
  const parameters = { event_category: 'engagement', tour: props.tour.slug, page_path: window.location.pathname }
  if (typeof window.gtag === 'function') window.gtag('event', name, parameters)
  else { window.dataLayer ||= []; window.dataLayer.push({ event: name, ...parameters }) }
}

const go = (i: number) => {
  if (i < 0 || i >= props.tour.steps.length || i === index.value) return
  index.value = i
  if (!started.value) { started.value = true; track('tour_start') }
  if (i === props.tour.steps.length - 1) track('tour_complete')
}

// Arrow keys move between tabs (WAI-ARIA tabs pattern).
const onTabKey = (event: KeyboardEvent, i: number) => {
  const last = props.tour.steps.length - 1
  const next = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? Math.min(i + 1, last)
    : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? Math.max(i - 1, 0)
    : event.key === 'Home' ? 0 : event.key === 'End' ? last : null
  if (next === null) return
  event.preventDefault()
  go(next)
  nextTick(() => document.getElementById(`${uid}-tab-${props.tour.steps[next].id}`)?.focus())
}
</script>

<style scoped>
/* The app's neutrals (#0a0a0a ink, #e6e6e6 hairlines) with the site teal for the current step. */
.st {
  --st-ink: #0a0a0a; --st-ink2: #4f4f4f; --st-ink3: #717171; --st-edge: #e5e7eb; --st-canvas: #ffffff;
  --st-page: #f8fafc; --st-accent: #0e7490; --st-accent-ink: #ffffff; --st-tint: #f0f9fc;
  display: grid;
  gap: 1.25rem;
  color: var(--st-ink);
}
:global(.dark .st) {
  --st-ink: #fafafa; --st-ink2: #c2c2c2; --st-ink3: #9a9a9a; --st-edge: #2c2c2c; --st-canvas: #1a1a1a;
  --st-page: #121212; --st-accent: #67e8f9; --st-accent-ink: #0b1220; --st-tint: rgb(103 232 249 / 0.07);
}

.st-steps { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 0.4rem; }
.st-step {
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.2rem;
  min-height: 2.75rem; padding: 0.35rem 0.25rem; border: 1px solid var(--st-edge); border-radius: 8px;
  background: var(--st-canvas); color: var(--st-ink2); font-size: 0.72rem; font-weight: 700; line-height: 1.15;
  cursor: pointer; transition: border-color 160ms ease, background-color 160ms ease, color 160ms ease;
}
.st-step:hover { border-color: var(--st-ink3); }
.st-step:focus-visible { outline: 2px solid var(--st-accent); outline-offset: 2px; }
.st-step-num {
  display: inline-grid; place-items: center; width: 1.4rem; height: 1.4rem; border-radius: 5px;
  background: var(--st-page); color: var(--st-ink2); font-size: 0.75rem; font-variant-numeric: tabular-nums;
}
.st-step.is-done .st-step-num { color: var(--st-accent); }
.st-step.is-active { border-color: var(--st-accent); background: var(--st-tint); color: var(--st-ink); }
.st-step.is-active .st-step-num { background: var(--st-accent); color: var(--st-accent-ink); }
/* Phones show only the numbers; the current step's label is the caption's count line. */
.st-step-label { display: none; }

.st-panel { display: grid; gap: 1.25rem; min-width: 0; }

.st-stage { margin: 0; overflow: hidden; border: 1px solid var(--st-edge); border-radius: 10px; background: var(--st-canvas); }
.st-bar { display: flex; gap: 0.35rem; padding: 0.6rem 0.75rem; border-bottom: 1px solid var(--st-edge); background: var(--st-page); }
.st-bar span { width: 0.55rem; height: 0.55rem; border-radius: 50%; background: var(--st-edge); }
/* One fixed frame for every step (phones: 4:5, the phone renders' top; desktop: 16:10), so stepping never shifts the page. */
.st-screens { position: relative; aspect-ratio: 4 / 5; background: #ffffff; }
.st-shot { position: absolute; inset: 0; opacity: 0; transition: opacity 220ms ease; }
.st-shot.is-current { opacity: 1; }
.st-shot img { width: 100%; height: 100%; object-fit: cover; object-position: top center; }
/* A desktop-only screen on a phone shows its top-left corner at a readable scale instead of shrinking it. */
.st-shot--desktop-only img { object-position: top left; }

.st-copy { min-width: 0; }
.st-count { color: var(--st-ink3); font-size: 0.78rem; font-weight: 700; font-variant-numeric: tabular-nums; }
.st-caption { margin-top: 0.35rem; font-size: 1.25rem; font-weight: 750; line-height: 1.25; text-wrap: balance; }
.st-sub { margin-top: 0.5rem; max-width: 34rem; color: var(--st-ink2); font-size: 0.95rem; line-height: 1.6; }
.st-controls { display: flex; gap: 0.6rem; margin-top: 1.1rem; }
.st-btn {
  display: inline-flex; flex: 1 1 auto; min-height: 2.75rem; align-items: center; justify-content: center;
  border: 1px solid var(--st-edge); border-radius: 8px; padding: 0.6rem 1rem; font-size: 0.92rem; font-weight: 750;
  color: var(--st-ink); background: var(--st-canvas); cursor: pointer;
}
.st-btn:disabled { opacity: 0.45; cursor: default; }
.st-btn:focus-visible { outline: 2px solid var(--st-accent); outline-offset: 2px; }
.st-btn--ghost { flex: 0 0 auto; }
.st-btn--next { border-color: var(--st-ink); }
.st-btn--trial { border-color: var(--st-accent); background: var(--st-accent); color: var(--st-accent-ink); }

@media (prefers-reduced-motion: reduce) {
  .st-shot, .st-step { transition: none; }
}

@media (min-width: 900px) {
  .st { grid-template-columns: minmax(0, 15rem) minmax(0, 1fr); gap: 2rem; align-items: start; }
  .st-steps { grid-template-columns: minmax(0, 1fr); gap: 0.5rem; }
  .st-step { flex-direction: row; justify-content: flex-start; gap: 0.7rem; padding: 0.7rem 0.85rem; font-size: 0.9rem; text-align: left; }
  .st-step-label { display: inline; }
  .st-panel { grid-template-columns: minmax(0, 1fr); }
  .st-screens { aspect-ratio: 16 / 10; }
  /* Desktop screens are shown whole, letterboxed on the page tone, so every caption matches what is visible. */
  .st-shot img { object-fit: contain; object-position: center; background: var(--st-page); }
  .st-btn { flex: 0 0 auto; }
}
</style>
